import type { Config } from '@/config';
import {
  AuthenticationError,
  NetworkError,
  UsageLimitError,
  UsageLimitData,
} from '@/types';
import { VERSION } from '@/version';

// Upload endpoint relative to base API URL
const UPLOAD_ENDPOINT = '/api/reports/playwright';

/**
 * Response payload from the TestDino API when uploading a report
 */
export interface ReportUploadResponse {
  testRunId: string;
  organizationId?: string;
  projectId?: string;
  viewUrl?: string;
  status?: string;
  warnings?: string[];
}

/**
 * Client for communicating with the TestDino API
 */
export class ApiClient {
  private readonly baseUrl: string;

  private readonly apiKey: string;

  constructor(config: Config) {
    this.baseUrl = config.apiUrl;
    this.apiKey = config.token;
  }

  /**
   * Headers to include on every API request
   */
  private getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'User-Agent': `tdpw/${VERSION}`,
      'X-API-Key': this.apiKey,
    };
  }

  /**
   * Upload a JSON payload to the TestDino API with enhanced error handling
   * @param payload The data to upload (report + metadata)
   */
  async uploadReport(payload: unknown): Promise<ReportUploadResponse> {
    const url = `${this.baseUrl}${UPLOAD_ENDPOINT}`;
    let response: Response;

    try {
      response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(payload),
      });
    } catch (error) {
      // Handle network-level errors (DNS, connection refused, etc.)
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown network error';

      throw new NetworkError(
        `Failed to connect to TestDino API: ${errorMessage}`
      );
    }

    // Handle HTTP error responses
    if (!response.ok) {
      await this.handleHttpError(response);
    }

    // Parse JSON response
    let json: unknown;
    try {
      json = await response.json();
    } catch (error) {
      throw new NetworkError(
        `Invalid JSON response from API: ${error instanceof Error ? error.message : 'Parse error'}`
      );
    }

    // Extract response data
    const result = this.parseUploadResponse(json);
    return result;
  }

  /**
   * Handle HTTP error responses with detailed error messages
   */
  private async handleHttpError(response: Response): Promise<never> {
    let errorBody: string;
    try {
      errorBody = await response.text();
    } catch {
      errorBody = 'Unable to read error response';
    }

    switch (response.status) {
      case 401:
        throw new AuthenticationError('Invalid API key or unauthorized access');

      case 403:
        throw new AuthenticationError(
          'API key does not have permission to upload reports'
        );

      case 400:
        throw new NetworkError(
          `Bad request - Invalid data format: ${errorBody}`
        );

      case 413:
        throw new NetworkError(
          'Report payload too large - consider uploading without HTML/traces'
        );

      case 429:
        throw new NetworkError(
          'Rate limit exceeded - please wait before retrying'
        );

      case 500:
      case 502:
      case 503:
      case 504:
        throw new NetworkError(
          `TestDino API server error (${response.status}) - please try again later`
        );

      default:
        throw new NetworkError(
          `HTTP ${response.status}: ${errorBody || response.statusText}`
        );
    }
  }

  /**
   * Parse and validate the upload response
   */
  private parseUploadResponse(json: unknown): ReportUploadResponse {
    if (!json || typeof json !== 'object') {
      throw new NetworkError('Invalid response format - expected JSON object');
    }

    // Check for usage limit error (HTTP 200 with success: false)
    this.checkForUsageLimitError(json);

    // Handle different response structures
    let responseData: ReportUploadResponse;

    // Check for direct response
    if ('testRunId' in json) {
      responseData = json as ReportUploadResponse;
    }
    // Check for wrapped response
    else if (
      'data' in json &&
      json.data &&
      typeof json.data === 'object' &&
      'testRunId' in json.data
    ) {
      responseData = json.data as ReportUploadResponse;
    }
    // Check for success wrapper
    else if (
      'success' in json &&
      'result' in json &&
      json.result &&
      typeof json.result === 'object' &&
      'testRunId' in json.result
    ) {
      responseData = json.result as ReportUploadResponse;
    } else {
      throw new NetworkError(
        `Unexpected API response structure: ${JSON.stringify(json)}`
      );
    }

    // Validate required fields
    if (!responseData.testRunId || typeof responseData.testRunId !== 'string') {
      throw new NetworkError('API response missing required testRunId field');
    }

    // Build response object
    const result: ReportUploadResponse = {
      testRunId: responseData.testRunId,
    };

    // Add optional fields if present
    if (
      responseData.organizationId &&
      typeof responseData.organizationId === 'string'
    ) {
      result.organizationId = responseData.organizationId;
    }

    if (responseData.projectId && typeof responseData.projectId === 'string') {
      result.projectId = responseData.projectId;
    }

    if (responseData.viewUrl && typeof responseData.viewUrl === 'string') {
      result.viewUrl = responseData.viewUrl;
    }

    if (responseData.status && typeof responseData.status === 'string') {
      result.status = responseData.status;
    }

    // Extract warnings array if present
    if (Array.isArray(responseData.warnings)) {
      result.warnings = responseData.warnings.filter(
        (w): w is string => typeof w === 'string'
      );
    }

    return result;
  }

  /**
   * Check for usage limit error in API response
   * Server returns HTTP 200 with success: false and either:
   * - data.code: "QUOTA_LIMIT_EXCEEDED" (with full usage data)
   * - message containing quota-related keywords (minimal response)
   */
  private checkForUsageLimitError(json: unknown): void {
    if (!json || typeof json !== 'object') return;

    const response = json as Record<string, unknown>;

    // Only check if success is explicitly false
    if (response.success !== false) return;

    const message =
      typeof response.message === 'string' ? response.message : '';
    const lowerMessage = message.toLowerCase();

    // Check for usage limit error by code or message pattern
    const data =
      response.data && typeof response.data === 'object'
        ? (response.data as Record<string, unknown>)
        : {};

    const isUsageLimitError =
      data.code === 'QUOTA_LIMIT_EXCEEDED' ||
      lowerMessage.includes('limit reached') ||
      lowerMessage.includes('test case limit') ||
      lowerMessage.includes('allocation exhausted') ||
      lowerMessage.includes('quota') ||
      lowerMessage.includes('execution allocation');

    if (isUsageLimitError) {
      const usageData: UsageLimitData = {
        tier: typeof data.tier === 'string' ? data.tier : 'unknown',
        limit: typeof data.limit === 'number' ? data.limit : 0,
        used: typeof data.used === 'number' ? data.used : 0,
        remaining: typeof data.remaining === 'number' ? data.remaining : 0,
        resetDate:
          typeof data.resetDate === 'string'
            ? data.resetDate
            : new Date().toISOString(),
      };

      // Add organizationId if present in response
      if (typeof data.organizationId === 'string') {
        usageData.organizationId = data.organizationId;
      }

      throw new UsageLimitError(message || 'Usage limit exceeded.', usageData);
    }
  }

  /**
   * Health check endpoint to verify API connectivity
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/health`, {
        method: 'GET',
        headers: {
          'User-Agent': `tdpw/${VERSION}`,
        },
      });
      return response.ok;
    } catch {
      return false;
    }
  }
}
